import React, { useRef, useEffect, useCallback, useState } from 'react';
import { styled } from '@mui/system';
import { useTheme } from '@mui/material/styles';
import { transformForForceDirected, calculateSkillRelationships } from './visualizationUtils';

const GalaxyWrapper = styled('div')(({ theme }) => ({
  width: '100%',
  maxWidth: '100%',
  // Fallback for browsers that don't support dvh
  height: '100vh',
  // Use dynamic viewport height for mobile where supported
  '@supports (height: 100dvh)': {
    height: '100dvh'
  },
  position: 'relative',
  backgroundColor: theme.palette.white.main, // White background
  overflow: 'hidden',
  touchAction: 'pan-y pinch-zoom', // Allow vertical scrolling and pinch zoom
  boxSizing: 'border-box',
  [theme.breakpoints.down('sm')]: {
    height: '-webkit-fill-available', // Fallback for older mobile browsers
    width: '100%',
    maxWidth: '100%'
  }
}));


const GalaxyView = ({ skills, onSkillClick, lang = 'EN' }) => {
  const canvasRef = useRef(null);
  const animationFrameRef = useRef(null);
  const theme = useTheme();
  const mouseRef = useRef({ x: 0, y: 0, isDown: false, lastX: 0, lastY: 0 });
  const panRef = useRef({ x: 0, y: 0 });
  const zoomRef = useRef(1);
  const hoveredStarRef = useRef(null);
  const [, setZoom] = useState(1);
  const hasCalculatedFit = useRef(false);
  const initializedRef = useRef(false);
  const skillsRef = useRef(skills);

  // Physics simulation state
  const simulationRef = useRef({
    nodes: [],
    links: [],
    width: 0,
    height: 0,
    alpha: 1
  });

  const calculateFitZoom = useCallback(() => {
    const sim = simulationRef.current;
    if (!sim || sim.nodes.length === 0) return 1;

    const { nodes, width, height } = sim;
    
    // Find bounding box
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    nodes.forEach((node) => {
      minX = Math.min(minX, node.x);
      minY = Math.min(minY, node.y);
      maxX = Math.max(maxX, node.x);
      maxY = Math.max(maxY, node.y);
    });

    // Add padding
    const padding = 50;
    const contentWidth = maxX - minX + padding * 2;
    const contentHeight = maxY - minY + padding * 2;

    // Calculate zoom to fit
    const zoomX = width / contentWidth;
    const zoomY = height / contentHeight;
    const fitZoom = Math.min(zoomX, zoomY, 0.8); // Max 80% to ensure some padding

    // Center the view
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;
    panRef.current.x = width / 2 - centerX * fitZoom;
    panRef.current.y = height / 2 - centerY * fitZoom;

    return fitZoom;
  }, []);

  const initializeGalaxy = useCallback(() => {
    if (!skills || skills.length === 0) return;

    const relationships = calculateSkillRelationships(skills, 3);
    const { nodes, links } = transformForForceDirected(skills, relationships);
    
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    
    // Reset zoom and fit calculation
    hasCalculatedFit.current = false;
    zoomRef.current = 1;
    setZoom(1);
    panRef.current = { x: 0, y: 0 };
    
    // Spread nodes much more widely initially for less grouping
    const spreadFactor = Math.max(rect.width, rect.height) * 0.9; // Use 90% of viewport size for wider spread
    
    simulationRef.current = {
      nodes: nodes.map((node) => ({
        ...node,
        // Much wider initial spread - use full viewport area
        x: rect.width / 2 + (Math.random() - 0.5) * spreadFactor,
        y: rect.height / 2 + (Math.random() - 0.5) * spreadFactor,
        vx: (Math.random() - 0.5) * 0.5, // Add small initial velocity for more spread
        vy: (Math.random() - 0.5) * 0.5,
        starSize: Math.max(3, (node.radius || 5) / 1.5),
        twinkleOffset: Math.random() * Math.PI * 2,
        color: theme.palette.primary.main
      })),
      links,
      width: rect.width,
      height: rect.height,
      alpha: 1
    };
  }, [skills, theme]);

  const updatePhysics = useCallback(() => {
    const sim = simulationRef.current;
    if (!sim || sim.nodes.length === 0) return;

    const isStable = sim.alpha < 0.01;
    
    // Calculate fit zoom once when physics stabilizes
    if (isStable && !hasCalculatedFit.current) {
      const fitZoom = calculateFitZoom();
      zoomRef.current = fitZoom;
      setZoom(fitZoom);
      hasCalculatedFit.current = true;
    }

    const { nodes, links, width, height } = sim;
    const k = Math.sqrt((width * height) / nodes.length);
    
    // 300x slower forces with much weaker gravity (3x slower than previous)
    // Increased repulsion and link distance for less grouping
    const chargeStrength = isStable ? -0.167 : -0.5; // 3x weaker
    const linkDistance = 300; // Increased from 200 to 300 for even less grouping
    const linkStrength = isStable ? 0.00033 : 0.0008; // Reduced link strength for less grouping
    const centerGravity = isStable ? 0.00001 : 0.000027; // Much weaker gravity (3x weaker)

    // Add subtle continuous drift when stable
    const time = Date.now() / 10000; // Slow time factor

    nodes.forEach((node, index) => {
      // Repulsion (300x slower)
      nodes.forEach((other) => {
        if (node === other) return;
        const dx = node.x - other.x;
        const dy = node.y - other.y;
        const distance = Math.sqrt(dx * dx + dy * dy) || 1;
        // Increased repulsion radius for less grouping
        if (distance < 500) { // Increased from 400 to 500 for even less grouping
          const force = (chargeStrength * k) / (distance * distance);
          const forceMultiplier = isStable ? 0.001 : 0.0033; // 3x slower
          node.vx += (dx / distance) * force * forceMultiplier;
          node.vy += (dy / distance) * force * forceMultiplier;
        }
      });

      // Link attraction (300x slower)
      links.forEach((link) => {
        const source = nodes.find((n) => n.id === link.source);
        const target = nodes.find((n) => n.id === link.target);
        if (source !== node && target !== node) return;
        
        const other = source === node ? target : source;
        const dx = other.x - node.x;
        const dy = other.y - node.y;
        const distance = Math.sqrt(dx * dx + dy * dy) || 1;
        const force = (distance - linkDistance) * linkStrength;
        const forceMultiplier = isStable ? 0.00067 : 0.00033; // 3x slower
        
        node.vx += (dx / distance) * force * forceMultiplier;
        node.vy += (dy / distance) * force * forceMultiplier;
      });

      // Center gravity (much weaker and 300x slower)
      node.vx += (width / 2 - node.x) * centerGravity;
      node.vy += (height / 2 - node.y) * centerGravity;

      // Add subtle continuous drift when stable (300x slower)
      if (isStable) {
        const driftSpeed = 0.00033; // 3x slower drift
        const angle = (index / nodes.length) * Math.PI * 2 + time;
        node.vx += Math.cos(angle) * driftSpeed * 0.1;
        node.vy += Math.sin(angle) * driftSpeed * 0.1;
      }

      // Apply velocity with even more damping (300x slower movement)
      const damping = isStable ? 0.99993 : 0.9997; // Even more damping = 3x slower
      node.vx *= damping;
      node.vy *= damping;
      node.x += node.vx;
      node.y += node.vy;
    });

    // Much slower alpha decay for very gentle movement
    if (!isStable) {
      sim.alpha *= 0.99993; // 3x slower decay
    } else {
      // Keep a minimal alpha to maintain continuous movement
      sim.alpha = 0.0000033; // 3x smaller
    }
  }, [calculateFitZoom]);

  const drawRef = useRef(null);
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const sim = simulationRef.current;
    if (!sim || !sim.nodes || sim.nodes.length === 0) return;
    const { nodes, links, width, height } = sim;

    ctx.clearRect(0, 0, width, height);

    ctx.save();
    ctx.translate(panRef.current.x, panRef.current.y);
    ctx.scale(zoomRef.current, zoomRef.current);

    // Draw Links
    ctx.lineWidth = 1;
    links.forEach((link) => {
      const source = nodes.find((n) => n.id === link.source);
      const target = nodes.find((n) => n.id === link.target);
      if (!source || !target) return;

      const isConnectedToHover = hoveredStarRef.current && 
        (hoveredStarRef.current.id === source.id || hoveredStarRef.current.id === target.id);
      
      // Use salad green for lines
      const color = theme.palette.primary.main;
      const opacity = hoveredStarRef.current
        ? (isConnectedToHover ? 0.8 : 0.1)
        : 0.3; // More visible by default on white bg

      ctx.strokeStyle = color;
      ctx.globalAlpha = opacity;
      ctx.beginPath();
      ctx.moveTo(source.x, source.y);
      ctx.lineTo(target.x, target.y);
      ctx.stroke();
      ctx.globalAlpha = 1;
    });

    // Draw Stars and Labels
    nodes.forEach((node) => {
      const isHovered = hoveredStarRef.current && hoveredStarRef.current.id === node.id;
      const isNeighbor = hoveredStarRef.current && links.some(l => 
        (l.source === hoveredStarRef.current.id && l.target === node.id) ||
        (l.target === hoveredStarRef.current.id && l.source === node.id)
      );

      let size = node.starSize;
      let alpha = 1;
      let labelAlpha = 0.7; // Default label visibility
      let labelSize = 12;

      if (hoveredStarRef.current) {
        if (isHovered) {
          size = node.starSize * 1.5;
          labelAlpha = 1;
          labelSize = 14;
        } else if (isNeighbor) {
          size = node.starSize * 1.2;
          labelAlpha = 0.9;
        } else {
          alpha = 0.3;
          labelAlpha = 0.1; // Fade out labels of unrelated nodes
        }
      }

      // Draw Glow (Salad Green)
      if (alpha > 0.5) {
        const glow = ctx.createRadialGradient(node.x, node.y, 0, node.x, node.y, size * 3);
        const glowColor = theme.palette.primary.main;
        
        // Simple hex to rgb
        const r = parseInt(glowColor.slice(1, 3), 16);
        const g = parseInt(glowColor.slice(3, 5), 16);
        const b = parseInt(glowColor.slice(5, 7), 16);

        glow.addColorStop(0, `rgba(${r}, ${g}, ${b}, ${alpha * 0.3})`);
        glow.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);
        ctx.fillStyle = glow;
        ctx.beginPath();
        ctx.arc(node.x, node.y, size * 3, 0, Math.PI * 2);
        ctx.fill();
      }

      // Draw Star Core (Salad Green)
      ctx.fillStyle = theme.palette.primary.main;
      ctx.globalAlpha = alpha;
      ctx.beginPath();
      ctx.arc(node.x, node.y, size, 0, Math.PI * 2);
      ctx.fill();
      
      // Draw Label
      ctx.font = `${isHovered ? '600' : '400'} ${labelSize}px Rubik-Regular`;
      ctx.fillStyle = theme.palette.textPrimary.main; // Dark text for labels
      ctx.globalAlpha = labelAlpha;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      // Add some padding from the dot
      ctx.fillText(node.name, node.x + size + 8, node.y);
      
      ctx.globalAlpha = 1;
    });

    ctx.restore();

    updatePhysics();
    animationFrameRef.current = requestAnimationFrame(draw);
  }, [theme, updatePhysics]);
  
  // Store draw in ref
  useEffect(() => {
    drawRef.current = draw;
  }, [draw]);

  useEffect(() => {
    // Only initialize if skills have actually changed
    const skillsChanged = !initializedRef.current || 
      !skillsRef.current || 
      skillsRef.current.length !== skills?.length ||
      (skillsRef.current.length > 0 && skillsRef.current[0]?._id !== skills?.[0]?._id);
    
    if (skillsChanged && skills && skills.length > 0) {
      skillsRef.current = skills;
      initializeGalaxy();
      initializedRef.current = true;
    }
    
    const canvas = canvasRef.current;
    if (canvas) {
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;
    }
    
    // Use ref to avoid dependency on draw callback
    if (drawRef.current) {
      drawRef.current();
    }
    
    return () => cancelAnimationFrame(animationFrameRef.current);
  }, [skills, initializeGalaxy]);

  const handleMouseMove = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    if (mouseRef.current.isDown) {
      panRef.current.x += e.clientX - mouseRef.current.lastX;
      panRef.current.y += e.clientY - mouseRef.current.lastY;
    }

    mouseRef.current.lastX = e.clientX;
    mouseRef.current.lastY = e.clientY;

    // Account for zoom when calculating local coordinates
    const localX = (x - panRef.current.x) / zoomRef.current;
    const localY = (y - panRef.current.y) / zoomRef.current;
    
    const star = simulationRef.current.nodes.find(n => {
      const dx = localX - n.x;
      const dy = localY - n.y;
      return Math.sqrt(dx * dx + dy * dy) < 20 / zoomRef.current; 
    });

    if (star !== hoveredStarRef.current) {
      hoveredStarRef.current = star;
      if (star) {
        canvas.style.cursor = 'pointer';
      } else {
        canvas.style.cursor = mouseRef.current.isDown ? 'grabbing' : 'grab';
      }
    }

  }, []);

  const handleMouseDown = useCallback((e) => {
    // Only capture mouse if it's a left click (not right click or middle button)
    if (e.button === 0) {
      mouseRef.current.isDown = true;
      mouseRef.current.lastX = e.clientX;
      mouseRef.current.lastY = e.clientY;
      e.preventDefault(); // Prevent text selection when dragging
    }
  }, []);

  const handleMouseUp = useCallback(() => {
    mouseRef.current.isDown = false;
  }, []);

  const handleClick = useCallback((e) => {
    if (hoveredStarRef.current && onSkillClick) {
      onSkillClick(hoveredStarRef.current.skill);
    }
  }, [onSkillClick]);

  const handleWheel = useCallback((e) => {
    // Only prevent default if Ctrl/Cmd is held (zoom gesture)
    // Otherwise allow normal page scrolling
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? 0.9 : 1.1;
      const newZoom = Math.max(0.3, Math.min(3, zoomRef.current * delta));
      zoomRef.current = newZoom;
      setZoom(newZoom);
    }
    // If Ctrl/Cmd not held, let the event bubble for page scrolling
  }, []);

  if (!skills || skills.length === 0) return null;

  return (
    <GalaxyWrapper>
      <canvas
        ref={canvasRef}
        onMouseMove={handleMouseMove}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onClick={handleClick}
        onWheel={handleWheel}
        style={{ 
          width: '100%', 
          height: '100%', 
          display: 'block',
          touchAction: 'pan-y pinch-zoom' // Allow vertical scrolling
        }}
      />
    </GalaxyWrapper>
  );
};

export default GalaxyView;
